import { z } from 'zod';

import { ZodIntegerAmountValue } from '@Common/types/Amount';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodBrandedDateString } from '@Common/types/zod/zod-branded-date';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';

import type { BoListQueryBase } from '@Common/types/bo/bo_list_query';

export const ZodB2bCorrectionStatus = z.enum([
  'preparing',
  'awaiting_approval',
  'approved',
  'rejected',
]);
export type B2bCorrectionStatus = z.TypeOf<typeof ZodB2bCorrectionStatus>;

export const ZodB2bCorrectionType = z.enum([
  'pending_orders',
  'pending_orders_manual',
  'filled_orders',
]);

export type B2bCorrectionType = z.TypeOf<typeof ZodB2bCorrectionType>;

export const ZodB2bCorrectionSource = z.enum([
  'standard_correction',
  'voluntary_deposit_cancellation',
]);

export type B2bCorrectionSource = z.TypeOf<typeof ZodB2bCorrectionSource>;

const ZodGetCorrectionsItem = z.object({
  id: ZodId,
  b2b_company_id: ZodId.nullable(),
  status: ZodB2bCorrectionStatus,
  start_date: ZodBrandedDateString.nullable(),
  end_date: ZodBrandedDateString,
  // Null for a voluntary deposit cancellation, which is not approved through a task
  bo_task_id: ZodId.nullable(),
  created_at: z.date(),
  updated_at: z.date(),
  type: ZodB2bCorrectionType,
  source: ZodB2bCorrectionSource,
});

export type GetCorrectionsItem = z.TypeOf<typeof ZodGetCorrectionsItem>;

const ZodGetCorrectionsResponse = z.array(ZodGetCorrectionsItem);

export type GetCorrectionsResponse = z.TypeOf<typeof ZodGetCorrectionsResponse>;

export const getB2bOpsCorrectionSortColumns: (keyof GetCorrectionsItem)[] = [
  'id',
  'b2b_company_id',
  'status',
  'start_date',
  'end_date',
  'created_at',
  'updated_at',
  'type',
  'source',
];

const ZodGetCorrectionsFilters = z
  .object({
    id: createZodArrayable(ZodId),
    b2b_company_id: createZodArrayable(ZodId),
    status: createZodArrayable(ZodB2bCorrectionStatus),
    type: createZodArrayable(ZodB2bCorrectionType),
    source: createZodArrayable(ZodB2bCorrectionSource),
  })
  .partial();

type GetCorrectionsFilters = z.TypeOf<typeof ZodGetCorrectionsFilters>;

export const ZodGetCorrectionsQuery = buildZodBoListQueryParsed(
  ZodGetCorrectionsFilters,
  getB2bOpsCorrectionSortColumns,
  {
    range: [0, 49],
    sort: ['id', 'DESC'],
  },
);

export type GetB2bOpsCorrectionQuery = BoListQueryBase<
  GetCorrectionsFilters,
  (typeof getB2bOpsCorrectionSortColumns)[number]
>;

export const getCorrectionsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetCorrectionsQuery,
  responseBody: ZodGetCorrectionsResponse,
});

export type GetCorrectionsEndpoint = GetEndpointType<typeof getCorrectionsEndpoint>;

export type GetB2bOpsCorrectionEndpoint = GetEndpointType<typeof getCorrectionsEndpoint>;

export const ZodPostCorrectionsBodyPendingOrders = z.object({
  type: z.literal('pending_orders'),
  b2b_company_id: ZodId,
  employee_ids: z.array(ZodId).min(1),
  start_date: ZodBrandedDateString.nullable(),
  end_date: ZodBrandedDateString,
});

export const ZodB2bFilledOrderCorrectionOperationType = z.enum([
  'matching_contribution',
  'campaign',
  'voluntary_payment',
]);

export type B2bFilledOrderCorrectionOperationType = z.TypeOf<
  typeof ZodB2bFilledOrderCorrectionOperationType
>;

export const ZodB2bFilledOrderCorrectionType = z.enum(['amount', 'entry_fee']);

export type B2bFilledOrderCorrectionType = z.TypeOf<typeof ZodB2bFilledOrderCorrectionType>;

const ZodFilledOrderRow = z.object({
  od_order_id: ZodId,
  user_currency_amount_input: ZodIntegerAmountValue,
  entry_fee_amount: ZodIntegerAmountValue,
  employer_tax_amount: ZodIntegerAmountValue,
});

const ZodFilledOrderEntryFeeRow = z.object({
  od_order_id: ZodId,
  entry_fee_employee_amount: ZodIntegerAmountValue.refine((amount) => amount >= 0, {
    message: 'entry_fee_employee_amount must not be negative',
  }),
});

const ZodPostCorrectionsBodyFilledOrdersBase = {
  type: z.literal('filled_orders'),
  operation_type: ZodB2bFilledOrderCorrectionOperationType,
  responsibility: z.literal('partner'),
  expected_nav: z.literal('original'),
  should_reinvest_entry_fee_difference: z.boolean(),
};

const ZodPostCorrectionsBodyFilledOrdersAmount = z.object({
  ...ZodPostCorrectionsBodyFilledOrdersBase,
  correction_type: z.literal('amount'),
  orders: z.array(ZodFilledOrderRow).min(1),
});

const ZodPostCorrectionsBodyFilledOrdersEntryFee = z.object({
  ...ZodPostCorrectionsBodyFilledOrdersBase,
  correction_type: z.literal('entry_fee'),
  orders: z.array(ZodFilledOrderEntryFeeRow).min(1),
});

export const ZodPostCorrectionsBodyFilledOrders = z.discriminatedUnion('correction_type', [
  ZodPostCorrectionsBodyFilledOrdersAmount,
  ZodPostCorrectionsBodyFilledOrdersEntryFee,
]);

export const ZodB2bCorrectionManagementType = z.enum(['LIBRE', 'PILOTE']);

export type B2bCorrectionManagementType = z.TypeOf<typeof ZodB2bCorrectionManagementType>;

const ZodManualOrderRow = z
  .object({
    employee_id: ZodId,
    campaign_id: ZodId.nullable(),
    management_type: ZodB2bCorrectionManagementType,
    od_order_id: ZodId.nullable(),
    fully_managed_plan_contribution_id: ZodId.nullable(),
    instrument_id: ZodId.nullable(),
    gross_amount: ZodIntegerAmountValue,
    net_amount: ZodIntegerAmountValue,
    csg_amount: ZodIntegerAmountValue,
    crds_amount: ZodIntegerAmountValue,
  })
  .superRefine((row, ctx) => {
    if (row.management_type === 'LIBRE') {
      if (row.instrument_id === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'LIBRE row must have an instrument_id',
          path: ['instrument_id'],
        });
      }
      if (row.od_order_id === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'LIBRE row must have an od_order_id',
          path: ['od_order_id'],
        });
      }
      if (row.fully_managed_plan_contribution_id !== null) {
        ctx.addIssue({
          code: 'custom',
          message: 'LIBRE row must not have a fully_managed_plan_contribution_id',
          path: ['fully_managed_plan_contribution_id'],
        });
      }
    } else {
      if (row.fully_managed_plan_contribution_id === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'PILOTE row must have a fully_managed_plan_contribution_id',
          path: ['fully_managed_plan_contribution_id'],
        });
      }
      if (row.od_order_id !== null) {
        ctx.addIssue({
          code: 'custom',
          message: 'PILOTE row must not have an od_order_id',
          path: ['od_order_id'],
        });
      }
    }

    const expectedGross = row.net_amount + row.csg_amount + row.crds_amount;
    if (expectedGross !== row.gross_amount) {
      ctx.addIssue({
        code: 'custom',
        message: `gross (${row.gross_amount}) != net + csg + crds (${expectedGross}) [cents]`,
        path: ['gross_amount'],
      });
    }
  });

export const ZodPostCorrectionsBodyPendingOrdersManual = z.object({
  type: z.literal('pending_orders_manual'),
  b2b_company_id: ZodId,
  orders: z.array(ZodManualOrderRow).min(1),
});

const ZodPostCorrectionsBodyDiscriminated = z.union([
  ZodPostCorrectionsBodyPendingOrders,
  ZodPostCorrectionsBodyPendingOrdersManual,
  ZodPostCorrectionsBodyFilledOrdersAmount,
  ZodPostCorrectionsBodyFilledOrdersEntryFee,
]);

export type PostCorrectionsBodyPendingOrders = z.TypeOf<typeof ZodPostCorrectionsBodyPendingOrders>;

export type PostCorrectionsBodyPendingOrdersManual = z.TypeOf<
  typeof ZodPostCorrectionsBodyPendingOrdersManual
>;

export type PostCorrectionsOrderManualRow = z.TypeOf<typeof ZodManualOrderRow>;

export type PostCorrectionsBodyFilledOrders = z.TypeOf<typeof ZodPostCorrectionsBodyFilledOrders>;

export type PostCorrectionsBodyFilledOrdersAmount = z.TypeOf<
  typeof ZodPostCorrectionsBodyFilledOrdersAmount
>;

export type PostCorrectionsBodyFilledOrdersEntryFee = z.TypeOf<
  typeof ZodPostCorrectionsBodyFilledOrdersEntryFee
>;

export type PostCorrectionsOrderRow = z.TypeOf<typeof ZodFilledOrderRow>;

export type PostCorrectionsOrderEntryFeeRow = z.TypeOf<typeof ZodFilledOrderEntryFeeRow>;

export type PostCorrectionsBody = z.TypeOf<typeof ZodPostCorrectionsBodyDiscriminated>;

export const postCorrectionsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostCorrectionsBodyDiscriminated,
  responseBody: z.object({ id: ZodId }),
});

export type PostCorrectionsEndpoint = GetEndpointType<typeof postCorrectionsEndpoint>;

const ZodGetCorrectionEmployee = z.object({
  employee_id: ZodId,
  firstname: z.string().nullable(),
  lastname: z.string().nullable(),
  plans: z.array(z.string()),
});

export type GetCorrectionEmployee = z.TypeOf<typeof ZodGetCorrectionEmployee>;

const ZodGetCorrectionByIdResponse = ZodGetCorrectionsItem.extend({
  employees: z.array(ZodGetCorrectionEmployee),
  filled_order_operation_type: ZodB2bFilledOrderCorrectionOperationType.nullable(),
  filled_order_correction_type: ZodB2bFilledOrderCorrectionType.nullable(),
  is_filled_order_reinvest_entry_fee_diff: z.boolean().nullable(),
  failed_orders_count: z.number().int(),
});

export type GetCorrectionByIdResponse = z.TypeOf<typeof ZodGetCorrectionByIdResponse>;

export const getCorrectionByIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetCorrectionByIdResponse,
});

export type GetCorrectionByIdEndpoint = GetEndpointType<typeof getCorrectionByIdEndpoint>;

export const ZodB2bCorrectionOrderStatus = z.enum(['pending', 'completed', 'failed']);

export type B2bCorrectionOrderStatus = z.TypeOf<typeof ZodB2bCorrectionOrderStatus>;

const ZodGetCorrectionOrderItem = z.object({
  id: ZodId,
  b2b_correction_id: ZodId,
  od_order_id: ZodId.nullable(),
  status: ZodB2bCorrectionOrderStatus,
  error_reason: z.string().nullable(),
  user_currency_amount_input_before: ZodIntegerAmountValue.nullable(),
  gross_amount_before: ZodIntegerAmountValue.nullable(),
  entry_fee_amount_before: ZodIntegerAmountValue.nullable(),
  employer_tax_amount_before: ZodIntegerAmountValue.nullable(),
  // Exactly one of these is set, depending on the correction type: filled_orders and
  // pending_orders carry a user_currency_amount_input, pending_orders_manual a gross_amount.
  user_currency_amount_input_after: ZodIntegerAmountValue.nullable(),
  gross_amount_after: ZodIntegerAmountValue.nullable(),
  entry_fee_amount_after: ZodIntegerAmountValue.nullable(),
  employer_tax_amount_after: ZodIntegerAmountValue.nullable(),
  already_corrected: z.boolean(),
  created_order_ids: z.array(ZodId),
  matched_contribution_order_id: ZodId.nullable(),
  instrument_id: ZodId.nullable(),
  fully_managed_plan_contribution_id: ZodId.nullable(),
  net_amount: ZodIntegerAmountValue.nullable(),
  csg_amount: ZodIntegerAmountValue.nullable(),
  crds_amount: ZodIntegerAmountValue.nullable(),
});

export type GetCorrectionOrderItem = z.TypeOf<typeof ZodGetCorrectionOrderItem>;

const ZodGetCorrectionOrdersResponse = z.array(ZodGetCorrectionOrderItem);

export type GetCorrectionOrdersResponse = z.TypeOf<typeof ZodGetCorrectionOrdersResponse>;

export const getB2bOpsCorrectionOrderSortColumns: (keyof GetCorrectionOrderItem)[] = [
  'id',
  'b2b_correction_id',
  'od_order_id',
  'user_currency_amount_input_after',
  'gross_amount_after',
  'entry_fee_amount_after',
  'employer_tax_amount_after',
];

const ZodGetCorrectionOrdersFilters = z
  .object({
    b2b_correction_id: createZodArrayable(ZodId),
  })
  .partial();

type GetCorrectionOrdersFilters = z.TypeOf<typeof ZodGetCorrectionOrdersFilters>;

export const ZodGetCorrectionOrdersQuery = buildZodBoListQueryParsed(
  ZodGetCorrectionOrdersFilters,
  getB2bOpsCorrectionOrderSortColumns,
  {
    range: [0, 49],
    sort: ['id', 'ASC'],
  },
);

export type GetB2bOpsCorrectionOrderQuery = BoListQueryBase<
  GetCorrectionOrdersFilters,
  (typeof getB2bOpsCorrectionOrderSortColumns)[number]
>;

export const getCorrectionOrdersEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetCorrectionOrdersQuery,
  responseBody: ZodGetCorrectionOrdersResponse,
});

export type GetCorrectionOrdersEndpoint = GetEndpointType<typeof getCorrectionOrdersEndpoint>;

export type GetB2bOpsCorrectionOrderEndpoint = GetEndpointType<typeof getCorrectionOrdersEndpoint>;
